import React, { useMemo } from 'react'
import { Button, Card, Collapse, Spin, Table, Tag } from 'antd'
import { ClockCircleOutlined } from '@ant-design/icons'
import styled from 'styled-components/macro'
import moment from 'moment'

const { Panel } = Collapse

const ScheduledUploadsSection = ({
  selectedPriceFileId,
  scheduledUploads = [],
  isLoading = false,
  isCollapsed = true,
  hasFetched = false,
  onToggleCollapsed,
  onFetch,
  onCancelUpload,
  onDismissUpload,
}) => {
  const sortedUploads = useMemo(
    () =>
      [...scheduledUploads].sort(
        (a, b) =>
          moment(a.scheduledFor).valueOf() - moment(b.scheduledFor).valueOf(),
      ),
    [scheduledUploads],
  )

  const handleCollapseChange = async keys => {
    const isOpen = keys.length > 0
    if (typeof onToggleCollapsed === 'function') {
      onToggleCollapsed(!isOpen)
    }

    if (
      isOpen &&
      !hasFetched &&
      selectedPriceFileId &&
      typeof onFetch === 'function'
    ) {
      await onFetch(selectedPriceFileId)
    }
  }

  return (
    <ScheduledUploadsCard style={{ marginBottom: 24 }}>
      <Collapse
        activeKey={isCollapsed ? [] : ['scheduled-uploads']}
        onChange={handleCollapseChange}
      >
        <Panel
          key='scheduled-uploads'
          header={
            <ScheduledUploadsHeader>
              <ClockCircleOutlined style={{ fontSize: 18, marginRight: 8 }} />
              Scheduled Uploads
              {sortedUploads.length > 0 && (
                <Tag color='blue' style={{ marginLeft: 8, fontSize: 12 }}>
                  {sortedUploads.length}
                </Tag>
              )}
            </ScheduledUploadsHeader>
          }
        >
          {isLoading ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <Spin tip='Loading scheduled uploads...' />
            </div>
          ) : sortedUploads.length === 0 ? (
            <ScheduledUploadsEmpty>
              <ScheduledUploadsEmptyTitle>
                No scheduled uploads
              </ScheduledUploadsEmptyTitle>
              <ScheduledUploadsEmptyText>
                Schedule your price file updates to go live automatically at a
                future date and time. Perfect for planning price changes in
                advance.
              </ScheduledUploadsEmptyText>
            </ScheduledUploadsEmpty>
          ) : (
            <Table
              dataSource={sortedUploads}
              columns={[
                {
                  title: 'Price File Name',
                  dataIndex: 'priceFileName',
                  key: 'priceFileName',
                },
                {
                  title: 'Scheduled For',
                  dataIndex: 'scheduledFor',
                  key: 'scheduledFor',
                  render: date => {
                    if (!date) return '-'
                    const dateMoment = moment.isMoment(date)
                      ? date
                      : moment(date)
                    return dateMoment.format('YYYY-MM-DD HH:mm [GMT]')
                  },
                },
                {
                  title: 'Created',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  render: date => {
                    if (!date) return '-'
                    const dateMoment = moment.isMoment(date)
                      ? date
                      : moment(date)
                    return dateMoment.format('YYYY-MM-DD HH:mm [GMT]')
                  },
                },
                {
                  title: 'Created By',
                  dataIndex: 'createdBy',
                  key: 'createdBy',
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: status => {
                    const statusConfig = {
                      pending: { color: 'blue', label: 'Pending' },
                      applied: { color: 'green', label: 'Applied' },
                      cancelled: { color: 'default', label: 'Cancelled' },
                      failed: { color: 'red', label: 'Failed' },
                    }
                    const config = statusConfig[status] || {
                      color: 'default',
                      label: status,
                    }
                    return <Tag color={config.color}>{config.label}</Tag>
                  },
                },
                {
                  title: 'Action',
                  key: 'action',
                  render: (_, record) => {
                    if (
                      record.status === 'applied' ||
                      record.status === 'cancelled'
                    ) {
                      return (
                        <Button
                          type='link'
                          size='small'
                          onClick={() =>
                            typeof onDismissUpload === 'function' &&
                            onDismissUpload(record._id)
                          }
                        >
                          Dismiss
                        </Button>
                      )
                    }

                    const actionLabel =
                      record.status === 'failed' ? 'Delete' : 'Cancel'
                    return (
                      <Button
                        type='link'
                        danger
                        size='small'
                        onClick={() =>
                          typeof onCancelUpload === 'function' &&
                          onCancelUpload(record._id)
                        }
                      >
                        {actionLabel}
                      </Button>
                    )
                  },
                },
              ]}
              pagination={false}
              size='small'
              rowKey='_id'
            />
          )}
        </Panel>
      </Collapse>
    </ScheduledUploadsCard>
  )
}

const ScheduledUploadsCard = styled(Card)`
  .ant-collapse {
    border: none;
    background: transparent;
  }
  .ant-collapse-item {
    border: none;
  }
  .ant-collapse-header {
    padding: 0 0 12px 0 !important;
  }
`

const ScheduledUploadsHeader = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
`

const ScheduledUploadsEmpty = styled.div`
  text-align: center;
  padding: 24px 8px;
`

const ScheduledUploadsEmptyTitle = styled.div`
  font-weight: 600;
  margin-bottom: 6px;
`

const ScheduledUploadsEmptyText = styled.div`
  color: #666;
  max-width: 520px;
  margin: 0 auto;
`

export default ScheduledUploadsSection
